import { z } from 'zod';
/**
 * Shape of the files `scripts/export_web_dataset.py` writes into `web/datasets`.
 *
 * The Python side is the source of truth: it joins the built artifact, the
 * Polish prose and the chess reviewer's grades under the same rules the print
 * layout uses. This schema exists so nothing malformed reaches the database,
 * and so the front end can name the fields it renders.
 */
export const SCHEMA_VERSION = 2;
export const poleSide = z.enum(['left', 'right']);
export const bucket = z.enum(['dominant', 'counter']);
/** Where a variation comes from: the game itself, or the engine. */
export const lineSource = z.enum(['game', 'engine']);
export const moveSchema = z.object({
    san: z.string(),
    uci: z.string().regex(/^[a-h][1-8][a-h][1-8][qrbn]?$/),
    /** Polish name of the pole this move classifies on. */
    pole: z.string(),
    winPct: z.number(),
    lossWinPct: z.number(),
    /** Maia's probability for this move, in permille; null when uncovered. */
    policyPermille: z.number().nullable(),
    line: z.array(z.string()),
    lineSource,
});
export const boardSchema = z.object({
    id: z.string(),
    /** "Dowód N" as printed, counted after boards without prose are dropped. */
    ordinal: z.number().int().positive(),
    bucket,
    gameId: z.string(),
    ply: z.number().int().nonnegative(),
    moveNumber: z.number().int().positive(),
    color: z.enum(['w', 'b']),
    fen: z.string(),
    moveLabel: z.string(),
    heading: z.string(),
    played: moveSchema,
    alternative: moveSchema,
    /**
     * One entry per wording of this board. Usually one; two when an A/B round
     * is running and a second wording was written for this position.
     */
    commentaries: z
        .array(z.object({
        variant: z.string(),
        played: z.string(),
        alternative: z.string(),
        /**
         * Who wrote this wording (e.g. "agent", "deepseek"). Admin-only
         * provenance for reading A/B results per model; the reviewer flow
         * never sees it, so the blinding holds.
         */
        generator: z.string().optional(),
    }))
        .min(1),
    context: z.object({
        phase: z.string(),
        positionState: z.string(),
        opportunityType: z.string(),
        rootWinPct: z.number(),
        weight: z.number(),
    }),
    /** Deterministic inputs behind the prose; keys vary by axis. */
    facts: z.record(z.string(), z.string()),
    review: z.object({
        /** "31|Wxe8+|Wad1", the key the grades are filed under. */
        key: z.string(),
        rating: z.number().int().nullable(),
        /** Printed with "(?)": the owner wants this one questioned. */
        flagged: z.boolean(),
        /** Never graded by the chess reviewer, so still awaiting an eye. */
        fresh: z.boolean(),
    }),
});
export const axisSchema = z.object({
    key: z.string(),
    ordinal: z.number().int().positive(),
    title: z.string(),
    poleLeft: z.string(),
    poleRight: z.string(),
    note: z.string(),
    dominantPole: poleSide,
    reading: z.object({
        player: z.number().nullable(),
        maia: z.number().nullable(),
        difference: z.number().nullable(),
        differenceCiLow: z.number().nullable(),
        differenceCiHigh: z.number().nullable(),
        differenceSignificant: z.boolean(),
        ciLow: z.number().nullable(),
        ciHigh: z.number().nullable(),
        decisions: z.number().int(),
        games: z.number().int(),
        nEff: z.number(),
        direction: z.string(),
        /** False when the axis withholds its direction; the note says why. */
        publishable: z.boolean(),
        directionNote: z.string().nullable(),
    }),
    boards: z.array(boardSchema),
});
export const datasetSchema = z.object({
    schemaVersion: z.literal(SCHEMA_VERSION),
    slug: z.string().regex(/^[a-z0-9-]+$/),
    /** Initials, not the full name: this is what outside reviewers see. */
    label: z.string(),
    playerName: z.string(),
    revision: z.string(),
    layout: z.enum(['print', 'screen']),
    band: z.string(),
    games: z.number().int(),
    decisions: z.number().int(),
    composition: z.record(z.string(), z.unknown()),
    testNote: z.string(),
    reviewerQuestions: z.array(z.string()),
    source: z.object({
        artifact: z.string(),
        artifactSha256: z.string().length(64),
        comments: z.string(),
        commentsSha256: z.string().length(64),
        commentsB: z.string().nullable(),
        commentsBSha256: z.string().length(64).nullable(),
        review: z.string().nullable(),
        reviewSha256: z.string().length(64).nullable(),
    }),
    totals: z.object({
        axes: z.number().int(),
        boards: z.number().int(),
        flagged: z.number().int(),
        fresh: z.number().int(),
        withSecondVariant: z.number().int(),
    }),
    axes: z.array(axisSchema),
});
//# sourceMappingURL=dataset.js.map